import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from 'react-router-dom';
import logoImage from '../images/Logo-Base-basic.png';
import Sidebar from '../components/SideBar';
import { Dropdown } from 'react-bootstrap';
import { getAdminOrders, updateOrderStatus } from './orderViewHelper';
import { ENDPOINT_PATH } from '../config';
import './orderHistory.css';

/** Fulfillment order for STATUS column (unknown / legacy values sort last when ascending). */
const ORDER_STATUS_PIPELINE = ['Not Started', 'Shipped', 'Delivered', 'Cancelled'];


/** Popper uses fixed + menu is portaled to body so it stacks above expanded product rows. */
const ORDER_ROW_STATUS_MENU_POPPER = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: { boundary: 'viewport', padding: 8 },
    },
  ],
};

function tableColumnAriaSort(activeKey, activeDir, columnKey) {
  if (activeKey !== columnKey) return 'none';
  return activeDir === 'asc' ? 'ascending' : 'descending';
}

function OrderSortHeaderButton({ label, sortKey, activeKey, activeDir, onSort }) {
  const isPrimary = activeKey === sortKey;
  const showNeutral = !isPrimary;
  let arrows;
  if (showNeutral) {
    arrows = (
      <span className="order-sort-arrows order-sort-arrows--neutral" aria-hidden>
        <span className="order-sort-arrow-up">▲</span>
        <span className="order-sort-arrow-down">▼</span>
      </span>
    );
  } else if (activeDir === 'asc') {
    arrows = (
      <span className="order-sort-arrows" aria-hidden>
        <span className="order-sort-arrow-up order-sort-arrow-up--solo">▲</span>
      </span>
    );
  } else {
    arrows = (
      <span className="order-sort-arrows" aria-hidden>
        <span className="order-sort-arrow-down order-sort-arrow-down--solo">▼</span>
      </span>
    );
  }
  return (
    <button type="button" className="order-sort-btn" onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      {arrows}
    </button>
  );
}

function orderStatusRank(status) {
  const s = (status == null ? '' : String(status)).trim() || 'Unknown';
  const idx = ORDER_STATUS_PIPELINE.indexOf(s);
  return idx >= 0 ? idx : 999;
}

function orderPurchaseTimeMs(order) {
  const ts = order?.timeStamp;
  if (!ts) return 0;
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  if (ts instanceof Date && !Number.isNaN(ts.getTime())) return ts.getTime();
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
}

function orderProductCount(order) {
  const n = order?.finalProducts?.length;
  if (typeof n === 'number' && n > 0) return n;
  return 1;
}

function compareOrderStringsEmptyLast(sa, sb, asc) {
  const aEmpty = !sa || sa === '';
  const bEmpty = !sb || sb === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true });
  return asc ? cmp : -cmp;
}

function sortOrdersList(list, primaryKey, primaryDir, getOrderTotal) {
  const getOrderId = (o) => String(o?.id || '');
  const getEmail = (o) => String(o?.userEmail ?? '').trim();

  return [...list].sort((a, b) => {
    const asc = primaryDir === 'asc';
    let cmp = 0;

    if (primaryKey === 'orderId') {
      cmp = compareOrderStringsEmptyLast(getOrderId(a), getOrderId(b), asc);
    } else if (primaryKey === 'email') {
      cmp = compareOrderStringsEmptyLast(getEmail(a), getEmail(b), asc);
    } else if (primaryKey === 'products') {
      const diff = orderProductCount(a) - orderProductCount(b);
      cmp = asc ? diff : -diff;
    } else if (primaryKey === 'status') {
      const diff = orderStatusRank(a.status) - orderStatusRank(b.status);
      cmp = asc ? diff : -diff;
    } else if (primaryKey === 'total') {
      const diff = getOrderTotal(b) - getOrderTotal(a);
      if (!asc) {
        cmp = diff !== 0 ? diff : 0;
      } else if (diff !== 0) {
        cmp = -diff;
      }
    } else if (primaryKey === 'purchaseDate') {
      const diff = orderPurchaseTimeMs(b) - orderPurchaseTimeMs(a);
      if (!asc) {
        cmp = diff !== 0 ? diff : 0;
      } else if (diff !== 0) {
        cmp = -diff;
      }
    }

    if (cmp !== 0) return cmp;
    return getOrderId(b).localeCompare(getOrderId(a));
  });
}

const AdminNavbar = () => {
  return (
    <div className="navWrapper">
      <div className="AdminNav">
        <div>
          <a href="/" className="NavLink">
            <div className="AdminLogo d-flex justify-content-end">
              <img src={logoImage} alt='logo' className="NavLogo" />
            </div>
            <div className="AdminLogoText font-2 ps-2 fontLogo">mycustomframe</div>
          </a>
        </div>
      </div>
    </div>
  );
};

const OrderHistory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userType, setUserType] = useState(null);
  const itemsPerPage = 10;
  const [orderSort, setOrderSort] = useState({ key: 'purchaseDate', dir: 'desc' });

  // Status options for dropdowns
  const statusOptions = ["Not Started", "Shipped", "Delivered", "Cancelled"];

  // Check if user is admin or employee
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user'));

        if (!user || !user.uid) {
          alert('Please login to access the Orders page');
          navigate('/');
          return;
        }

        // Verify user is an admin or employee
        const response = await fetch(ENDPOINT_PATH + `/api/get-account-type?user=` + encodeURIComponent(user.uid));
        const accountType = await response.json();
        const accountTypeLower = accountType.toLowerCase();

        if (accountTypeLower !== 'admin' && accountTypeLower !== 'employee') {
          alert('Access denied: Admin or Employee account required');
          navigate('/');
        } else {
          setUserType(accountTypeLower);
        }
      } catch (error) {
        console.error("Error checking access:", error);
        alert('Error verifying access. Please try again.');
        navigate('/');
      }
    };

    checkAccess();
  }, [navigate]);

  // Helper functions for processing orders
  const processOrder = (order) => {
    return order; // userAddress is already in the order object
  };

  const processOrders = (ordersArray) => ordersArray.map((order) => processOrder(order));

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAdminOrders(selectedStatus, fromDate, toDate);

        // Process response based on its structure
        if (response && response.success && Array.isArray(response.data)) {
          setOrders(processOrders(response.data));
        } else if (response && response.data) {
          if (Array.isArray(response.data)) {
            setOrders(processOrders(response.data));
          } else {
            setOrders([processOrder(response.data)]);
          }
        } else if (Array.isArray(response)) {
          setOrders(processOrders(response));
        } else if (response && (response.id || response.finalProductDoc)) {
          setOrders([processOrder(response)]);
        } else {
          setOrders([]);
          setError("Received data is in an unexpected format");
        }
      } catch (err) {
        setError(`An error occurred while fetching orders: ${err.message}`);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, fromDate, toDate]);

  const handleResetStatus = () => setSelectedStatus("");
  const handleResetDates = () => {
    setFromDate("");
    setToDate("");
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Not Started": return "status-pending";
      case "Shipped": return "status-shipped";
      case "Delivered": return "status-delivered";
      case "Cancelled": return "status-cancelled";
      default: return "";
    }
  };

  // Handle status updates
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setStatusUpdating(orderId);

      // Call API to update status
      await updateOrderStatus(orderId, newStatus);

      // Update shipped status in dimensionsRequests
      await fetch(`${ENDPOINT_PATH}/api/update-shipped-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderID: orderId,
          status: newStatus
        })
      });

      // If status changed to Shipped, send shipping confirmation email
      if (newStatus === 'Shipped') {
        try {
          const emailResponse = await fetch(`${ENDPOINT_PATH}/api/send-shipping-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderID: orderId })
          });

          if (!emailResponse.ok) {
            console.error('Failed to send shipping email');
            alert(`Order ${orderId} status updated to ${newStatus}, but failed to send shipping confirmation email.`);
          } else {
            alert(`Order ${orderId} status updated to ${newStatus}. Shipping confirmation email sent to customer.`);
          }
        } catch (emailError) {
          console.error('Error sending shipping email:', emailError);
          alert(`Order ${orderId} status updated to ${newStatus}, but failed to send shipping confirmation email.`);
        }
      } else {
        // Display success feedback for non-Shipped statuses
        alert(`Order ${orderId} status updated to ${newStatus}`);
      }

      // Update the status in local state
      setOrders(orders.map(order => {
        if ((order.id || order.finalProductID) === orderId) {
          return { ...order, status: newStatus };
        }
        return order;
      }));

    } catch (error) {
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setStatusUpdating(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';

    const dateOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    try {
      let date;
      if (timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Invalid date format';
      }
      return date.toLocaleDateString('en-US', dateOptions);
    } catch (error) {
      return 'Date error';
    }
  };

  // Calculate total for a single product
  const calculateTotal = (product) => {
    if (!product) return { total: '0.00', breakdown: {} };

    try {
      let total = 0;
      const breakdown = {};

      if (product.framePrice && parseFloat(product.framePrice) > 0) {
        const framePrice = parseFloat(product.framePrice);
        total += framePrice;
        // Add frame thickness to the frame details
        breakdown.frame = {
          price: framePrice.toFixed(2),
          details: `${product.displayName || product.frameName || product.frameType || 'Custom Frame'} (${product.frameThickness || '-'}″ thick)`
        };
      }

      if (product.glassPrice && parseFloat(product.glassPrice) > 0) {
        const glassPrice = parseFloat(product.glassPrice);
        total += glassPrice;
        breakdown.glass = {
          price: glassPrice.toFixed(2),
          details: product.glassType || 'Standard Glass'
        };
      }

      if (product.hangPrice && parseFloat(product.hangPrice) > 0) {
        const hangPrice = parseFloat(product.hangPrice);
        total += hangPrice;
        breakdown.hanging = {
          price: hangPrice.toFixed(2),
          details: product.hangingStyle || product.hangingOption || 'Standard Hanging'
        };
      }

      if (product.mediumPrice && parseFloat(product.mediumPrice) > 0) {
        const mediumPrice = parseFloat(product.mediumPrice);
        total += mediumPrice;

        breakdown.medium = {
          price: mediumPrice.toFixed(2),
          details: product.medium || 'Photo Print'
        };

        if (product.medium === 'Canvas' && product.stretchingStyle) {
          breakdown.stretching = {
            price: "0.00",
            details: product.stretchingStyle
          };
        }
      }

      if (product.artistAlleyFee && parseFloat(product.artistAlleyFee) > 0) {
        const artistFee = parseFloat(product.artistAlleyFee);
        total += artistFee;
        breakdown.artistFee = {
          price: artistFee.toFixed(2),
          details: 'Artist Alley Fee'
        };
      }

      // Updated matboard handling to create separate entries for each matboard
      if (product.matboardPrices && Array.isArray(product.matboardPrices)) {
        // Get matboard thickness to add to details
        const matThickness = product.matboardThickness ? `${product.matboardThickness}″  thick` : '';

        if (product.selectedMatboards && Array.isArray(product.selectedMatboards)) {
          // Create individual entries for each matboard using the selectedMatboards array
          product.matboardPrices.forEach((price, index) => {
            const matPrice = parseMatboardPrice(price);
            if (matPrice > 0) {
              total += matPrice;

              const matColor = product.selectedMatboards[index]?.displayName || 'Custom Color';
              const matStyle = product.selectedMatboards[index]?.style || '';
              const matNumber = index + 1;

              breakdown[`matboard${matNumber}`] = {
                price: matPrice.toFixed(2),
                details: `${matColor} (${matStyle}${matThickness ? ', ' + matThickness : ''})`
              };
            }
          });
        } else if (product.matboardColors && Array.isArray(product.matboardColors)) {
          // Fallback to using matboardColors if available
          product.matboardPrices.forEach((price, index) => {
            const matPrice = parseMatboardPrice(price);
            if (matPrice > 0) {
              total += matPrice;

              const matColor = product.matboardColors[index] || 'Custom Color';
              const matNumber = index + 1;

              breakdown[`matboard${matNumber}`] = {
                price: matPrice.toFixed(2),
                details: `${matColor}${matThickness ? ' (' + matThickness + ')' : ''}`
              };
            }
          });
        } else {
          // Fallback if we have prices but no colors
          let matboardTotal = 0;
          product.matboardPrices.forEach(price => {
            const matPrice = parseMatboardPrice(price);
            if (matPrice > 0) matboardTotal += matPrice;
          });

          if (matboardTotal > 0) {
            total += matboardTotal;
            breakdown.matboard = {
              price: matboardTotal.toFixed(2),
              details: `Multiple Matboards (${product.matboardPrices.length})${matThickness ? ' (' + matThickness + ')' : ''}`
            };
          }
        }
      }

      return {
        total: total.toFixed(2),
        breakdown
      };
    } catch (error) {
      console.error("Error calculating product total:", error);
      return { total: '0.00', breakdown: {} };
    }
  };

  // Calculate order total with multiple products
  const calculateOrderTotal = (order) => {
    if (!order) return { total: '0.00', products: [] };

    try {
      // Check if order has finalProducts array
      if (order.finalProducts && Array.isArray(order.finalProducts)) {
        let orderTotal = 0;
        const productDetails = [];

        // Process each final product
        order.finalProducts.forEach((product, index) => {
          // Get quantity (default to 1 if not specified)
          const quantity = product.quantity || 1;

          // Calculate product price
          const priceData = calculateTotal(product.finalProductDoc);
          const productTotal = parseFloat(priceData.total) * quantity;
          orderTotal += productTotal;

          // Get the image URL and dimensions
          const imageUrl = getImageUrl(product.finalProductDoc);
          const dimensions = getDimensions(product.finalProductDoc);

          // Save product details
          productDetails.push({
            id: product.finalProductID || `Product-${index}`,
            title: product.finalProductDoc?.title || 'Custom Frame',
            quantity: quantity,
            priceData: priceData,
            productTotal: productTotal.toFixed(2),
            imageUrl: imageUrl,
            dimensions: dimensions
          });
        });

        return {
          total: orderTotal.toFixed(2),
          products: productDetails
        };
      } else if (order.finalProductDoc) {
        // Handle single product case
        const priceData = calculateTotal(order.finalProductDoc);
        const imageUrl = getImageUrl(order.finalProductDoc);
        const dimensions = getDimensions(order.finalProductDoc);

        return {
          total: priceData.total,
          products: [{
            id: order.finalProductID || 'Single-Product',
            title: order.finalProductDoc?.title || 'Custom Frame',
            quantity: 1,
            priceData: priceData,
            productTotal: priceData.total,
            imageUrl: imageUrl,
            dimensions: dimensions
          }]
        };
      }

      return { total: '0.00', products: [] };
    } catch (error) {
      console.error("Error calculating order total:", error);
      return { total: '0.00', products: [] };
    }
  };

  // Helper function to extract image URL from product document
  const getImageUrl = (productDoc) => {
    if (!productDoc) return null;
    return productDoc.uploadedImage ||
      productDoc.frameImage ||
      productDoc.imageUrl ||
      productDoc.originalImageUrl;
  };

  // Helper function to extract dimensions from product document
  const getDimensions = (productDoc) => {
    return {
      width: productDoc?.frameSize?.width ||
        productDoc?.width ||
        productDoc?.frameWidth || 'N/A',
      height: productDoc?.frameSize?.height ||
        productDoc?.height ||
        productDoc?.frameHeight || 'N/A'
    };
  };

  // Helper function to parse matboard price
  const parseMatboardPrice = (price) => {
    return typeof price === 'number' ? price : parseFloat(price);
  };

  // Helper function to handle image download
  const handleDownloadImage = (imageUrl, productTitle) => {
    if (!imageUrl) {
      alert('No image available for this product');
      return;
    }

    console.log("Attempting to open image in new tab:", imageUrl);

    // Check if image URL is valid
    const img = new Image();
    img.onerror = () => {
      console.error("Image failed to load:", imageUrl);
      alert('Unable to open image: The image URL appears to be invalid');
    };
    img.onload = () => {
      try {
        // Open the image in a new tab
        window.open(imageUrl, '_blank');
        console.log("Image opened in new tab:", imageUrl);
      } catch (err) {
        console.error("Open image error:", err);
        alert('Failed to open image in new tab. Please try again.');
      }
    };
    img.src = imageUrl;
  };

  const toggleOrderDetails = (orderId, event) => {
    // Prevent triggering when clicking other elements
    if (event.target.classList.contains('expand-arrow') ||
      event.target.classList.contains('expand-icon')) {
      if (expandedOrder === orderId) {
        setExpandedOrder(null);
      } else {
        setExpandedOrder(orderId);
      }
    }
  };

  const filteredOrders = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const base = orders.filter((order) =>
      searchQuery ? (order.userEmail || '').toLowerCase().includes(q) : true
    );
    const getOrderTotal = (order) => {
      const t = calculateOrderTotal(order).total;
      const n = parseFloat(String(t));
      return Number.isFinite(n) ? n : 0;
    };
    return sortOrdersList(base, orderSort.key, orderSort.dir, getOrderTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sort uses calculateOrderTotal from this render
  }, [orders, searchQuery, orderSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, fromDate, toDate]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
    setCurrentPage((p) => Math.min(Math.max(1, p), maxPage));
  }, [filteredOrders.length, itemsPerPage]);

  const handleOrderSortClick = useCallback((clickedKey) => {
    setCurrentPage(1);
    setOrderSort((prev) => {
      if (clickedKey === 'purchaseDate' || clickedKey === 'total') {
        if (prev.key === clickedKey) {
          return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
        }
        return { key: clickedKey, dir: 'desc' };
      }
      if (prev.key === clickedKey) {
        return { key: clickedKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: clickedKey, dir: 'asc' };
    });
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedOrders = filteredOrders.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const sortKey = orderSort.key;
  const sortDir = orderSort.dir;

  return (
    <div>
      <AdminNavbar />
      <hr className="admin-divider" />
      <div className="order-history-page">
        <Sidebar userType={userType || 'admin'} />
        <div className="order-history-content">
          <div className="header">
            <Dropdown>
              <Dropdown.Toggle variant="success" id="status-dropdown">
                {selectedStatus || "Order Status"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {statusOptions.map(status => (
                  <Dropdown.Item key={status} onClick={() => setSelectedStatus(status)}>
                    {status}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            {selectedStatus && (
              <button className="reset-btn" onClick={handleResetStatus} aria-label="Reset Status">
                &#x2715;
              </button>
            )}

            <div className="date-filters">
              <label htmlFor="fromDate" className="date-label">From</label>
              <input
                type="date"
                id="fromDate"
                className="date-filter"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                min="2000-01-01"
                max="2099-12-31"
                pattern="\\d{4}-\\d{2}-\\d{2}"
              />
              <label htmlFor="toDate" className="date-label">To</label>
              <input
                type="date"
                id="toDate"
                className="date-filter"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min="2000-01-01"
                max="2099-12-31"
                pattern="\\d{4}-\\d{2}-\\d{2}"
              />
              {(fromDate || toDate) && (
                <button className="reset-btn" onClick={handleResetDates} aria-label="Reset Dates">
                  &#x2715;
                </button>
              )}
            </div>

            <input
              type="text"
              className="search-box"
              placeholder="Search Email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="order-table-container">
            {loading ? (
              <div className="loading-text">Loading orders...</div>
            ) : error ? (
              <div className="error-text">{error}</div>
            ) : orders.length === 0 ? (
              <div className="no-data-text">No orders found.</div>
            ) : (
              <div className="orders-sortable-wrap">
              <table className="order-table">
                <thead>
                  <tr>
                    <th aria-hidden className="order-expand-th" />
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'orderId')}
                      className={`order-sort-th ${sortKey === 'orderId' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="ORDER ID"
                        sortKey="orderId"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'email')}
                      className={`order-sort-th ${sortKey === 'email' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="EMAIL"
                        sortKey="email"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                    <th scope="col">ADDRESS</th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'products')}
                      className={`order-sort-th ${sortKey === 'products' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="PRODUCTS"
                        sortKey="products"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'status')}
                      className={`order-sort-th ${sortKey === 'status' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="STATUS"
                        sortKey="status"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'total')}
                      className={`order-sort-th ${sortKey === 'total' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="TOTAL"
                        sortKey="total"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                    <th
                      scope="col"
                      aria-sort={tableColumnAriaSort(sortKey, sortDir, 'purchaseDate')}
                      className={`order-sort-th ${sortKey === 'purchaseDate' ? 'order-sort-th--active' : ''}`}
                    >
                      <OrderSortHeaderButton
                        label="PURCHASE DATE"
                        sortKey="purchaseDate"
                        activeKey={sortKey}
                        activeDir={sortDir}
                        onSort={handleOrderSortClick}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.length > 0 ? (
                    paginatedOrders.map((order, index) => {
                      const orderId = order.id || `Order-${index}`;
                      const orderData = calculateOrderTotal(order);
                      const isExpanded = expandedOrder === orderId;
                      const productCount = order.finalProducts?.length || 1;

                      const rowStatusMenu = (
                        <Dropdown.Menu
                          key={orderId}
                          className="order-row-status-dropdown-menu"
                          popperConfig={ORDER_ROW_STATUS_MENU_POPPER}
                        >
                          {statusOptions.map(status => (
                            <Dropdown.Item
                              key={status}
                              onClick={() => handleStatusChange(orderId, status)}
                              className={order.status === status ? "active" : ""}
                            >
                              {status}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      );

                      return (
                        <React.Fragment key={orderId}>
                          <tr className={isExpanded ? "expanded-row" : ""}>
                            <td className="expand-arrow" onClick={(e) => toggleOrderDetails(orderId, e)}>
                              <span className="expand-icon">
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            </td>
                            <td>{orderId}</td>
                            <td>{order.userEmail || 'N/A'}</td>
                            <td>{order.userAddress || 'N/A'}</td>
                            <td>{productCount} item{productCount !== 1 ? 's' : ''}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle
                                  className={`status-badge ${getStatusClass(order.status)}`}
                                  variant="link"
                                  id={`status-dropdown-${orderId}`}
                                  disabled={statusUpdating === orderId}
                                >
                                  {order.status || 'Unknown'}
                                </Dropdown.Toggle>
                                {typeof document !== 'undefined'
                                  ? createPortal(rowStatusMenu, document.body)
                                  : rowStatusMenu}
                              </Dropdown>
                            </td>
                            <td>${orderData.total}</td>
                            <td>{formatDate(order.timeStamp)}</td>
                          </tr>

                          {isExpanded && (
                            <tr className="price-breakdown-row">
                              <td></td>
                              <td colSpan="7">
                                <div className="order-breakdown">
                                  {orderData.products.map((product, productIndex) => (
                                    <div key={product.id} className="product-breakdown">
                                      <div className="product-header">
                                        <h4>Product {productIndex + 1}: {product.title}</h4>
                                        {product.quantity > 1 && (
                                          <p className="quantity-info">Quantity: {product.quantity}</p>
                                        )}
                                      </div>

                                      <div className="product-details-container">
                                        <div className="product-image-container">
                                          {product.imageUrl ? (
                                            <div className="image-section">
                                              <img
                                                src={product.imageUrl}
                                                alt={product.title}
                                                className="product-image"
                                                onError={(e) => {
                                                  console.log("Image failed to load:", product.imageUrl);
                                                  e.target.onerror = null;
                                                  e.target.src = "/placeholder-image.png";
                                                  e.target.style.opacity = "0.5";
                                                }}
                                              />
                                              <button
                                                className="download-btn"
                                                onClick={() => handleDownloadImage(product.imageUrl, product.title)}
                                              >
                                                Download Image
                                              </button>
                                            </div>
                                          ) : (
                                            <div className="no-image">
                                              <p>No image available</p>
                                            </div>
                                          )}
                                        </div>

                                        <div className="pricing-section">
                                          <table className="breakdown-table">
                                            <thead>
                                              <tr className="header-row">
                                                <th>Component</th>
                                                <th>Details</th>
                                                <th>Price</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {Object.entries(product.priceData.breakdown).map(([component, data], idx) => {
                                                // Format the component name for display
                                                console.log(product)
                                                console.log(product.priceData)
                                                let displayComponent = component;
                                                if (component.startsWith('matboard')) {
                                                  displayComponent = 'Matboard ' + component.replace('matboard', '');
                                                } else {
                                                  displayComponent = component.charAt(0).toUpperCase() + component.slice(1);
                                                }

                                                return (
                                                  <tr key={idx}>
                                                    <td>{displayComponent}</td>
                                                    <td>{data.details}</td>
                                                    <td>${data.price}</td>
                                                  </tr>
                                                );
                                              })}
                                              <tr className="subtotal-row">
                                                <td>
                                                  <strong style={{ color: '#ff4172' }}>
                                                    {product.quantity > 1 || orderData.products.length > 1 ? 'Product Subtotal' : 'Total'}
                                                  </strong>
                                                </td>
                                                <td></td>
                                                <td>
                                                  <strong style={{ color: '#ff4172' }}>${product.priceData.total}</strong>
                                                </td>
                                              </tr>
                                              {product.quantity > 1 && (
                                                <tr className="product-total-row">
                                                  <td><strong>Quantity × {product.quantity}</strong></td>
                                                  <td></td>
                                                  <td><strong>${product.productTotal}</strong></td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      {productIndex < orderData.products.length - 1 && (
                                        <hr className="product-divider" />
                                      )}
                                    </div>
                                  ))}

                                  {orderData.products.length > 1 && (
                                    <div className="order-total">
                                      <h4 style={{ color: '#ff4172' }}>Order Total: ${orderData.total}</h4>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">No orders match your filter criteria</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            )}

            {!loading && !error && filteredOrders.length > 0 && (
              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(safePage - 1)}
                  disabled={safePage <= 1 || filteredOrders.length === 0}
                >
                  Prev
                </button>
                <span>
                  {filteredOrders.length === 0
                    ? 'Showing 0 - 0 of 0'
                    : `Showing ${(safePage - 1) * itemsPerPage + 1} - ${Math.min(
                    safePage * itemsPerPage,
                    filteredOrders.length
                  )} of ${filteredOrders.length}`}
                </span>
                <button
                  className="page-btn"
                  onClick={() => handlePageChange(safePage + 1)}
                  disabled={safePage >= totalPages || filteredOrders.length === 0}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;